import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { API_BASE } from './config';
import ScraperHistory from './metasupply/pages/ScraperHistory';
import ProductsPage from './metasupply/pages/Products';
import ProductDetailPage from './metasupply/pages/ProductDetail';
import ClinicalDrugDetailPage from './metasupply/pages/ClinicalDrugDetail';
import DashboardPage from './metasupply/pages/Dashboard';
import HomeLauncher from './metasupply/pages/HomeLauncher';
import HomeLanding from './metasupply/pages/HomeLanding';
import SearchPage from './metasupply/pages/Search';
import IngestionPage from './metasupply/pages/Ingestion';
import Chatbot from './metasupply/components/Chatbot';
import ExpertAgent from './metasupply/components/ExpertAgent';
import SelfAttestationsPage from './metasupply/pages/SelfAttestations';
import MyAttestationsPage from './metasupply/pages/MyAttestations';
import ManufacturerListPage from './metasupply/pages/ManufacturerList';
import CountryOriginListPage from './metasupply/pages/CountryOriginList';
import ManufacturerDetail from './metasupply/pages/ManufacturerDetail';
import RiskAnalyzerPage from './metasupply/pages/RiskAnalyzer';
import ChinaDependencyPage from './metasupply/pages/ChinaDependency';
import GeopoliticalIntelligencePage from './metasupply/pages/GeopoliticalIntelligence';
import ShortageEarlyWarningPage from './metasupply/pages/ShortageEarlyWarning';
import ExecutiveBriefingsPage from './metasupply/pages/ExecutiveBriefings';
import ApiIntelligencePage from './metasupply/pages/ApiIntelligencePage';
import AgentActivityDashboard from './metasupply/pages/AgentActivityDashboard';
import AgentDetail from './metasupply/pages/AgentDetail';
import ScoredProducts from './metasupply/pages/ScoredProducts';
import FailedScoring from './metasupply/pages/FailedScoring';
import EvidenceDefinitions from './metasupply/pages/EvidenceDefinitions';
import SuggestedScoringImprovements from './metasupply/pages/SuggestedScoringImprovements';
import DataAnalyst from './metasupply/pages/DataAnalyst';
import StewardDataAnalyst from './metasupply/pages/StewardDataAnalyst';
import StewardProductUpdates from './metasupply/pages/StewardProductUpdates';
import StewardFailedUpdates from './metasupply/pages/StewardFailedUpdates';
import StewardTotalProducts from './metasupply/pages/StewardTotalProducts';
import DataOrchestratorDetail from './metasupply/pages/DataOrchestratorDetail';
import EvidenceHistory from './metasupply/pages/EvidenceHistory';
import PipelineToast from './metasupply/components/PipelineToast';
import MyAccount from './MyAccount';
import Admin from './Admin';
import { ScraperOverview, ScraperRun, ScraperError } from './metasupply/types/scraper';
import { Product, UnifiedSearchResults } from './metasupply/types/products';
import { DashboardStats, IARIData } from './metasupply/types/dashboard';
import { SearchResults } from './metasupply/types/search';
import { ClinicalDrug } from './metasupply/types/clinical';

// ============================================================
// iRemedy.ai - Medical Product Compliance Intelligence
// Visual Identity: Patriotic Premium - Navy + Gold
// ============================================================

// Shared components - extracted in A1.2
import { useTheme } from './metasupply/components/ThemeContext';
import {
  IconSearch, IconDashboard, IconDatabase, IconUpload, IconHistory,
  IconShield, IconChevron, IconGlobe, IconFlag, IconAlert,
  IconCheck, IconX, IconArrowLeft, IconSpinner, IconBot,
  IconStar, IconLogout, IconUser,
} from './metasupply/components/Icons';
import {
  IRemedyCross, FloatingPill, GlobeIcon,
  ScoreRing, StatCard, RiskBadge,
  SourceBadge, SectionSourceIndicator, SectionHeader,
} from './metasupply/components/SharedUI';
import { copyToClipboard } from './metasupply/components/utils';
import GlobalFilterBar from './metasupply/components/GlobalFilterBar';

// ============================================================
// TYPES
// ============================================================
interface User {
  id: number;
  email: string;
  is_admin: boolean;
}

// ============================================================
// MAIN APP PROPS
// ============================================================
interface MetaSupplyAppProps {
  user: User;
  onLogout: () => void;
}

// ============================================================
// ROUTE COMPONENTS (module-scope = stable component identity)
//
// These MUST live outside MetaSupplyApp. When they were defined inline inside
// the component body, each MetaSupplyApp re-render produced a brand-new
// component function (new type), so React unmounted + remounted the whole
// route subtree on every parent render. During the initial render storm
// (~20 renders: high-risk fetch, search, auth…) that remounted ProductDetailPage
// ~20× and re-ran its api-risk fetch each time ("page reloads ~20× then stops").
// Hoisting them gives a stable type → React reconciles instead of remounting,
// and the api-risk effect (dep [product.id]) no longer refires.
// ============================================================
interface ProductDetailRouteProps {
  selectedProduct: Product | null;
  setSelectedProduct: (p: Product | null) => void;
  loadProductDetail: (id: number) => void;
  loadClinicalDrugDetail: (id: number) => void;
  loadManufacturerDetail: (id: number) => void;
}

function ProductDetailRoute({
  selectedProduct,
  setSelectedProduct,
  loadProductDetail,
  loadClinicalDrugDetail,
  loadManufacturerDetail,
}: ProductDetailRouteProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const productId = id ? parseInt(id, 10) : null;
  const [localProduct, setLocalProduct] = useState<Product | null>(null);
  const [localLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    // If selectedProduct matches the URL id, use it; otherwise fetch
    if (selectedProduct && selectedProduct.id === productId) {
      setLocalProduct(selectedProduct);
      return;
    }
    if (!productId || isNaN(productId)) return;
    setLocalLoading(true);
    fetch(`${API_BASE}/metasupply/products/${productId}`, { credentials: 'include' })
      .then(r => { if (r.ok) return r.json(); throw new Error('Not found'); })
      .then(data => { setLocalProduct(data); setSelectedProduct(data); })
      .catch(() => setLocalProduct(null))
      .finally(() => setLocalLoading(false));
  }, [productId, selectedProduct, setSelectedProduct]);

  if (localLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
        <IconSpinner />
      </div>
    );
  }

  return (
    <ProductDetailPage
      product={localProduct}
      onBack={() => navigate("/products")}
      onViewPackaging={(pid) => { loadProductDetail(pid); }}
      onViewClinicalDrug={(cid) => loadClinicalDrugDetail(cid)}
      onViewManufacturer={loadManufacturerDetail}
      onViewProduct={(pid) => loadProductDetail(pid)}
      ScoreRing={ScoreRing}
      RiskBadge={RiskBadge}
      SourceBadge={SourceBadge}
      SectionSourceIndicator={SectionSourceIndicator}
      IconArrowLeft={IconArrowLeft}
      IconChevron={IconChevron}
    />
  );
}

interface ManufacturerDetailRouteProps {
  loadProductDetail: (id: number) => void;
  loadManufacturerDetail: (id: number) => void;
}

function ManufacturerDetailRoute({ loadProductDetail, loadManufacturerDetail }: ManufacturerDetailRouteProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [sp] = useSearchParams();
  const manufacturerId = id ? parseInt(id, 10) : null;
  const tabParam = sp.get('tab') as 'overview' | 'products' | 'recalls' | 'shortages' | 'fda-sites' | 'import-refusals' | 'ownership' | 'leadership' | 'ccp' | null;
  return (
    <ManufacturerDetail
      manufacturerId={manufacturerId}
      apiBase={API_BASE}
      onBack={() => navigate("/manufacturers")}
      onViewProduct={loadProductDetail}
      onViewManufacturer={loadManufacturerDetail}
      initialTab={tabParam || undefined}
      IconArrowLeft={IconArrowLeft}
      IconSpinner={IconSpinner}
      ScoreRing={ScoreRing}
    />
  );
}

// ============================================================
// MAIN APP COMPONENT
// ============================================================
export default function MetaSupplyApp({ user, onLogout }: MetaSupplyAppProps) {
  // Theme context
  const { T, getRiskColor, getMIAColor, getConfidenceColor } = useTheme();

  // Router hooks
  const navigate = useNavigate();
  const location = useLocation();

  // Derive current "view" from pathname for sidebar active state
  const getViewFromPath = (pathname: string): string => {
    if (pathname === '/') return 'home';
    if (pathname === '/supplychain') return 'home';
    if (pathname === '/dashboard') return 'dashboard';
    if (pathname === '/search') return 'search';
    if (pathname.startsWith('/products/')) return 'products';
    if (pathname === '/products') return 'products';
    if (pathname.startsWith('/clinical-drug/')) return 'products';
    if (pathname.startsWith('/manufacturers/')) return 'manufacturers';
    if (pathname === '/manufacturers') return 'manufacturers';
    if (pathname === '/country-origins') return 'country-origins';
    if (pathname === '/ingest') return 'ingest';
    if (pathname === '/scraper-history') return 'scraper-history';
    if (pathname === '/admin') return 'history';
    if (pathname === '/my-account') return 'my-account';
    if (pathname === '/self-attestations') return 'self-attestations';
    if (pathname === '/my-attestations') return 'my-attestations';
    if (pathname.startsWith('/risk-analyzer')) return 'risk-analyzer';
    if (pathname === '/apis') return 'apis';
    if (pathname === '/china-dependency') return 'china-dependency';
    if (pathname.startsWith('/geopolitical')) return 'geopolitical';
    if (pathname.startsWith('/shortage-warning')) return 'shortage-warning';
    if (pathname.startsWith('/briefings')) return 'briefings';
    if (pathname.startsWith('/agent-activity')) return 'agent-activity';
    if (pathname.startsWith('/agent-detail')) return 'agent-activity';
    return 'dashboard';
  };

  const view = getViewFromPath(location.pathname);
  const isHome = view === 'home';

  // State
  const [products, setProducts] = useState<Product[]>([]);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [iariData, setIariData] = useState<IARIData | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedClinicalDrug, setSelectedClinicalDrug] = useState<ClinicalDrug | null>(null);

  const loadManufacturerDetail = (id: number) => {
    navigate(`/manufacturers/${id}`);
  };
  const [unifiedSearch, setUnifiedSearch] = useState<UnifiedSearchResults | null>(null);
  const [unifiedSearchQuery, setUnifiedSearchQuery] = useState("");
  const [isUnifiedSearching, setIsUnifiedSearching] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [scraperOverview, setScraperOverview] = useState<ScraperOverview[]>([]);
  const [scraperRuns, setScraperRuns] = useState<ScraperRun[]>([]);
  const [scraperErrors, setScraperErrors] = useState<ScraperError[]>([]);
  const [scraperLoading, setScraperLoading] = useState(false);
  const [ingestionMode, setIngestionMode] = useState("ndc");
  const [rawInput, setRawInput] = useState("");
  const [ingestionUrl, setIngestionUrl] = useState("");
  const [isIngesting, setIsIngesting] = useState(false);
  const [ingestionResults, setIngestionResults] = useState<any>(null);
  const [ndcInput, setNdcInput] = useState("");
  const [drugNameInput, setDrugNameInput] = useState("");
  const [lookupStatus, setLookupStatus] = useState<Array<{msg: string; type: string; time: string}>>([]);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // File batch processing state
  const [batchResults, setBatchResults] = useState<Array<{
    rowIndex: number;
    drugName: string;
    ndc: string;
    status: 'pending' | 'processing' | 'success' | 'error';
    selected: boolean;
    expanded: boolean;
    expandedScrapers: Record<string, boolean>; // Track which scrapers are expanded
    result: any;
    error?: string;
  }>>([]);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [highRiskProducts, setHighRiskProducts] = useState<Product[]>([]);
  const [activityEvents, setActivityEvents] = useState<Array<{id: string; type: string; title: string; detail: string; status: string; timestamp: string | null}>>([]);
  const [productFilter, setProductFilter] = useState("");
  const [baseProducts, setBaseProducts] = useState<Product[]>([]);
  const [baseProductsTotal, setBaseProductsTotal] = useState(0);
  const [productsTotal, setProductsTotal] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Load data on mount
  useEffect(() => {
    loadDashboardData();
    loadProducts();
    loadTotals();
  }, []);

  // Load totals for stats bar
  const loadTotals = async () => {
    try {
      const response = await fetch(`${API_BASE}/metasupply/search?q=&limit=1`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setUnifiedSearch(data);
      }
    } catch (error) {
      console.error('Error loading totals:', error);
    }
  };

  const loadDashboardData = async () => {
    // Fetch all dashboard data in parallel so one failure doesn't block others
    const fetchStats = fetch(`${API_BASE}/metasupply/dashboard/stats`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setDashboardStats(data); })
      .catch(e => console.error('Error loading dashboard stats:', e));

    const fetchHighRisk = fetch(`${API_BASE}/metasupply/products/high-risk?limit=10`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.products) setHighRiskProducts(data.products); })
      .catch(e => console.error('Error loading high risk products:', e));

    const fetchActivity = fetch(`${API_BASE}/metasupply/dashboard/activity?limit=10`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.events) setActivityEvents(data.events); })
      .catch(e => console.error('Error loading activity:', e));

    const fetchIari = fetch(`${API_BASE}/metasupply/dashboard/iari`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setIariData(data); })
      .catch(e => console.error('Error loading IARI:', e));

    await Promise.allSettled([fetchStats, fetchHighRisk, fetchActivity, fetchIari]);
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/metasupply/products?limit=100`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products);
        setBaseProducts(data.products);
        const total = data.total ?? data.products.length;
        setProductsTotal(total);
        setBaseProductsTotal(total);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const query = productFilter.trim();
    if (!query) {
      setProducts(baseProducts);
      setProductsTotal(baseProductsTotal || baseProducts.length);
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${API_BASE}/metasupply/products?search=${encodeURIComponent(query)}&limit=500`,
          { credentials: 'include', signal: controller.signal }
        );
        if (response.ok) {
          const data = await response.json();
          setProducts(data.products);
          setProductsTotal(data.total ?? data.products.length);
        }
      } catch (error) {
        if ((error as any)?.name !== 'AbortError') {
          console.error('Error searching products:', error);
        }
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [productFilter, baseProducts, baseProductsTotal]);

  const loadProductDetail = async (productId: number) => {
    try {
      const response = await fetch(`${API_BASE}/metasupply/products/${productId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedProduct(data);
        navigate(`/products/${productId}`);
      }
    } catch (error) {
      console.error('Error loading product detail:', error);
    }
  };

  const loadScraperHistory = async () => {
    setScraperLoading(true);
    try {
      const [overviewRes, runsRes, errorsRes] = await Promise.all([
        fetch(`${API_BASE}/metasupply/scrapers/overview`, { credentials: 'include' }),
        fetch(`${API_BASE}/metasupply/scrapers/runs?limit=25`, { credentials: 'include' }),
        fetch(`${API_BASE}/metasupply/scrapers/errors?limit=25`, { credentials: 'include' }),
      ]);

      if (overviewRes.ok) {
        const data = await overviewRes.json();
        setScraperOverview(data.sources || []);
      }
      if (runsRes.ok) {
        const data = await runsRes.json();
        setScraperRuns(data.runs || []);
      }
      if (errorsRes.ok) {
        const data = await errorsRes.json();
        setScraperErrors(data.errors || []);
      }
    } catch (error) {
      console.error('Error loading scraper history:', error);
    } finally {
      setScraperLoading(false);
    }
  };

  useEffect(() => {
    if (location.pathname === "/scraper-history") {
      loadScraperHistory();
    }
  }, [location.pathname]);


  const loadClinicalDrugDetail = async (clinicalDrugId: number) => {
    try {
      const response = await fetch(`${API_BASE}/metasupply/clinical-drug/${clinicalDrugId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedClinicalDrug(data);
        navigate(`/clinical-drug/${clinicalDrugId}`);
      }
    } catch (error) {
      console.error('Error loading clinical drug detail:', error);
    }
  };

  // Unified search across products, packagings, and clinical drugs
  const handleUnifiedSearch = async (query: string) => {
    setUnifiedSearchQuery(query);
    if (!query.trim()) {
      setUnifiedSearch(null);
      return;
    }
    setIsUnifiedSearching(true);
    try {
      const response = await fetch(`${API_BASE}/metasupply/search?q=${encodeURIComponent(query)}&limit=20`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setUnifiedSearch(data);
      }
    } catch (error) {
      console.error('Error in unified search:', error);
    } finally {
      setIsUnifiedSearching(false);
    }
  };

  // Debounced unified search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (location.pathname === "/products" && unifiedSearchQuery) {
        handleUnifiedSearch(unifiedSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [unifiedSearchQuery, location.pathname]);

  // Toggle section collapse
  const toggleSection = (section: string) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // AI Search handler - uses existing query endpoint
  const handleSearch = async (queryOverride?: string) => {
    const q = queryOverride || searchQuery;
    if (!q.trim()) return;
    if (queryOverride) setSearchQuery(queryOverride);
    setIsSearching(true);
    setSearchResults(null);

    try {
      const response = await fetch(`${API_BASE}/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          question: q,
          explain: true,
        }),
      });

      if (response.status === 429) {
        const data = await response.json();
        setSearchResults({
          answer: '',
          insights: [],
          tokenLimitReached: true,
          tokenLimitMessage: data.detail || 'You have reached your monthly token limit. Please upgrade your plan to continue.',
        });
        setIsSearching(false);
        return;
      }

      const data = await response.json();

      if (data.success) {
        setSearchResults({
          answer: data.explanation || 'Query executed successfully.',
          sql: data.sql,
          results: data.results,
          resultCount: data.result_count,
          confidence: data.confidence,
          metadata: data.metadata,
          insights: [],
        });
      } else {
        setSearchResults({
          answer: data.error || 'Search encountered an error.',
          insights: [],
        });
      }
    } catch (error) {
      setSearchResults({
        answer: 'Network error. Please try again.',
        insights: [],
      });
    }

    setIsSearching(false);
  };

  // ============================================================
  // CORE API CALL - Reusable for single and batch modes
  // ============================================================
  const callLookupAgent = async (drugName: string, ndcCode: string | null): Promise<{
    success: boolean;
    data?: any;
    error?: string;
  }> => {
    try {
      const response = await fetch(`${API_BASE}/metasupply/lookup-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          drug_name: drugName,
          ndc_code: ndcCode || null,
        }),
      });
      const data = await response.json();
      if (data.success) {
        return {
          success: true,
          data: {
            type: "scraper_results",
            batch_id: data.batch_id,
            ndc_code: data.ndc_code,
            drug_name: data.drug_name,
            existing_product: data.existing_product,
            scrapers: data.scrapers,
            summary: data.summary
          }
        };
      } else {
        return { success: false, error: data.detail || 'Lookup failed' };
      }
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  // NDC Lookup Agent - Uses all 3 scrapers (SINGLE MODE)
  const handleNDCLookup = async () => {
    if (!drugNameInput.trim() && !ndcInput.trim()) return;
    setIsIngesting(true);
    setIngestionResults(null);
    setLookupStatus([]);

    const addStatus = (msg: string, type = "info") => {
      setLookupStatus(prev => [...prev, { msg, type, time: new Date().toLocaleTimeString() }]);
    };

    addStatus("Initiating multi-source lookup agent...", "info");
    addStatus(`Target: ${drugNameInput}${ndcInput ? ` (NDC: ${ndcInput})` : ""}`, "info");
    addStatus("Launching FDA NDC Scraper...", "search");
    addStatus("Launching FDA Shortage Scraper...", "search");
    addStatus("Launching Country of Origin Scraper...", "search");

    const result = await callLookupAgent(drugNameInput, ndcInput || null);

    if (result.success && result.data) {
      // Log results from each scraper
      for (const scraper of result.data.scrapers) {
        if (scraper.success) {
          addStatus(`✓ ${scraper.scraper_name}: Data retrieved (${scraper.duration_seconds}s)`, "success");
          if (scraper.staged) {
            addStatus(`  → Staged to database (ID: ${scraper.staging_id})`, "info");
          }
        } else {
          addStatus(`✗ ${scraper.scraper_name}: ${scraper.error || 'No data found'}`, "warning");
        }
      }

      addStatus(`Total API calls: ${result.data.summary.total_api_calls}`, "info");
      addStatus(`Total time: ${result.data.summary.total_duration_seconds}s`, "info");

      // Log existing product status
      if (result.data.existing_product) {
        addStatus(`📦 Product exists in DB (ID: ${result.data.existing_product.id})`, "warning");
      } else {
        addStatus(`✨ New product - will be added to database`, "success");
      }

      setIngestionResults(result.data);
    } else {
      addStatus(`Error: ${result.error || 'Lookup failed'}`, "error");
      setIngestionResults("error");
    }

    setIsIngesting(false);
  };

  // ============================================================
  // FILE BATCH PROCESSING
  // ============================================================
  const parseFileContent = (content: string, filename: string): Array<{drugName: string; ndc: string}> => {
    const rows: Array<{drugName: string; ndc: string}> = [];
    const lines = content.split(/\r?\n/).filter(line => line.trim());

    // Skip header if it looks like one
    const startIndex = lines[0]?.toLowerCase().includes('name') || lines[0]?.toLowerCase().includes('ndc') ? 1 : 0;

    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Try different delimiters: semicolon, comma, tab
      let parts: string[] = [];
      if (line.includes(';')) {
        parts = line.split(';').map(p => p.trim());
      } else if (line.includes('\t')) {
        parts = line.split('\t').map(p => p.trim());
      } else if (line.includes(',')) {
        parts = line.split(',').map(p => p.trim());
      } else {
        // Single column - assume it's NDC
        parts = ['', line];
      }

      // Format: name;ndc or ndc;name or just ndc
      if (parts.length >= 2) {
        // Clean potential NDC values (remove non-digit/dash characters for detection)
        const cleanFirst = parts[0].replace(/[^0-9-]/g, '');
        const cleanSecond = parts[1].replace(/[^0-9-]/g, '');

        // Detect which is NDC (contains digits and dashes in NDC pattern)
        const isFirstNDC = /^\d{4,5}-?\d{3,4}(-?\d{1,2})?$/.test(cleanFirst);
        const isSecondNDC = /^\d{4,5}-?\d{3,4}(-?\d{1,2})?$/.test(cleanSecond);

        if (isFirstNDC && !isSecondNDC) {
          rows.push({ drugName: parts[1] || '', ndc: cleanFirst });
        } else if (isSecondNDC) {
          rows.push({ drugName: parts[0], ndc: cleanSecond });
        } else {
          // Default: assume first is name, second is ndc
          rows.push({ drugName: parts[0], ndc: cleanSecond || cleanFirst });
        }
      } else if (parts.length === 1 && parts[0]) {
        const cleanNdc = parts[0].replace(/[^0-9-]/g, '');
        rows.push({ drugName: '', ndc: cleanNdc });
      }
    }
    return rows;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Read file content
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const parsed = parseFileContent(content, file.name);

      if (parsed.length === 0) {
        alert("No valid data found in file. Expected format: name;ndc or ndc per line");
        return;
      }

      // Initialize batch results
      setBatchResults(parsed.map((item, index) => ({
        rowIndex: index,
        drugName: item.drugName,
        ndc: item.ndc,
        status: 'pending',
        selected: true,
        expanded: false,
        expandedScrapers: {},
        result: null,
      })));
    };
    reader.readAsText(file);

    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleBatchProcess = async () => {
    if (batchResults.length === 0) return;

    setIsBatchProcessing(true);
    const total = batchResults.length;
    setBatchProgress({ current: 0, total });

    for (let i = 0; i < batchResults.length; i++) {
      const item = batchResults[i];

      // Update status to processing
      setBatchResults(prev => prev.map((r, idx) =>
        idx === i ? { ...r, status: 'processing' } : r
      ));
      setBatchProgress({ current: i + 1, total });

      // Call the SAME lookup agent function
      // Clean NDC: trim whitespace and remove any invisible characters
      const cleanNdc = item.ndc ? item.ndc.trim().replace(/[^\d\-]/g, '') : null;
      console.log(`[Batch ${i+1}] Calling lookup with drugName="${item.drugName}", ndc="${cleanNdc}"`);
      const result = await callLookupAgent(item.drugName, cleanNdc || null);

      // Update with result
      setBatchResults(prev => prev.map((r, idx) =>
        idx === i ? {
          ...r,
          status: result.success ? 'success' : 'error',
          result: result.data || null,
          error: result.error,
        } : r
      ));

      // Small delay to not overload API
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    setIsBatchProcessing(false);
  };

  const handleBatchCommit = async () => {
    // Commit only selected items that have results
    const toCommit = batchResults.filter(r => r.selected && r.status === 'success' && r.result?.batch_id);

    if (toCommit.length === 0) {
      alert("No items selected for commit");
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    for (const item of toCommit) {
      try {
        const response = await fetch(`${API_BASE}/metasupply/lookup-agent/commit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            batch_id: item.result.batch_id,
            scrapers: [],
          }),
        });
        const result = await response.json();
        if (result.success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch {
        errorCount++;
      }
    }

    alert(`Commit complete: ${successCount} success, ${errorCount} errors`);
  };

  const toggleAllSelected = (selected: boolean) => {
    setBatchResults(prev => prev.map(r => ({ ...r, selected })));
  };

  const toggleItemSelected = (index: number) => {
    setBatchResults(prev => prev.map((r, i) => i === index ? { ...r, selected: !r.selected } : r));
  };

  const toggleItemExpanded = (index: number) => {
    setBatchResults(prev => prev.map((r, i) => i === index ? { ...r, expanded: !r.expanded } : r));
  };

  const toggleScraperExpanded = (itemIndex: number, scraperSource: string) => {
    setBatchResults(prev => prev.map((r, i) => i === itemIndex ? {
      ...r,
      expandedScrapers: { ...r.expandedScrapers, [scraperSource]: !r.expandedScrapers[scraperSource] }
    } : r));
  };

  // Confirm ingestion - commits staging data to production
  const [commitStatus, setCommitStatus] = useState<{type: 'success' | 'error', message: string} | null>(null);

  const confirmIngestion = async () => {
    if (!ingestionResults) return;
    setCommitStatus(null);

    try {
      // If it's scraper results, use the commit endpoint
      if (ingestionResults.type === "scraper_results" && ingestionResults.batch_id) {
        const response = await fetch(`${API_BASE}/metasupply/lookup-agent/commit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            batch_id: ingestionResults.batch_id,
            scrapers: [], // Empty = commit all
          }),
        });

        const result = await response.json();
        if (result.success) {
          const totalCommitted = result.committed.products + result.committed.coo + result.committed.shortages;
          if (totalCommitted > 0) {
            setCommitStatus({
              type: 'success',
              message: `✓ Saved to database! ${result.committed.products} product(s), ${result.committed.coo} COO record(s), ${result.committed.shortages} shortage(s)`
            });
          } else {
            setCommitStatus({
              type: 'error',
              message: `No records were committed. Debug: staging had ${result.debug?.staging_products_count || 0} products, ${result.debug?.staging_coo_count || 0} COO records.`
            });
          }
          // Refresh products list
          await loadProducts();
          await loadDashboardData();

          // Clear form after short delay so user sees the message
          setTimeout(() => {
            setIngestionResults(null);
            setRawInput("");
            setIngestionUrl("");
            setNdcInput("");
            setDrugNameInput("");
            setLookupStatus([]);
            setCommitStatus(null);
          }, 3000);
        } else {
          setCommitStatus({
            type: 'error',
            message: `Error: ${result.detail || 'Failed to commit to database'}`
          });
        }
      } else if (Array.isArray(ingestionResults)) {
        // Legacy: array of products
        for (const product of ingestionResults) {
          await fetch(`${API_BASE}/metasupply/products/ingest`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              product_name: product.name,
              strength: product.strength,
              dosage_form: product.dosageForm,
              ndc_codes: product.ndc,
              manufacturer: product.manufacturer,
              manufacturer_hq: product.manufacturerHQ,
              api_source_country: product.apiSourceCountry,
              api_manufacturer: product.apiManufacturer,
              finished_goods_country: product.finishedGoodsMfg,
              finished_goods_plant: product.finishedGoodsPlant,
              made_in_america_score: product.madeInAmericaScore,
              baa_eligible: product.baaEligible,
              taa_eligible: product.taaEligible,
              supply_chain_risk: product.supplyChainRisk,
              risk_factors: product.riskFactors,
              therapeutic_class: product.therapeuticClass,
              confidence_score: product.confidenceScore,
              origin_confidence: product.originConfidence,
              origin_notes: product.originNotes,
            }),
          });
        }
        setCommitStatus({
          type: 'success',
          message: `✓ ${ingestionResults.length} product(s) saved to database!`
        });
        // Refresh and clear
        await loadProducts();
        await loadDashboardData();
        setTimeout(() => {
          setIngestionResults(null);
          setRawInput("");
          setIngestionUrl("");
          setNdcInput("");
          setDrugNameInput("");
          setLookupStatus([]);
          setCommitStatus(null);
        }, 3000);
      }
    } catch (error: any) {
      console.error('Error confirming ingestion:', error);
      setCommitStatus({
        type: 'error',
        message: `Error: ${error.message || 'Failed to save to database'}`
      });
    }
  };

  // Computed stats
  const stats = dashboardStats || {
    total_products: products.length,
    baa_eligible: products.filter(p => p.baa_eligible).length,
    high_risk: products.filter(p => p.supply_chain_risk === "high").length,
    avg_mia_score: products.length > 0 ? Math.round(products.reduce((a, p) => a + p.made_in_america_score, 0) / products.length) : 0,
    mia_confident_count: 0,
    domestic_api: products.filter(p => p.api_source_country === "United States").length,
    china_api: products.filter(p => p.api_source_country === "China").length,
    india_api: products.filter(p => p.api_source_country === "India").length,
    current_shortages: 0,
    data_freshness: null,
  };

  // Primary nav items (top bar) — restructured per Tony's UX update
  const navItems = [
    { id: "dashboard", path: "/dashboard", label: "Dashboard" },
    { id: "products", path: "/products", label: "Drugs" },
    { id: "apis", path: "/apis", label: "API's" },
    { id: "manufacturers", path: "/manufacturers", label: "Manufacturers" },
    { id: "china-dependency", path: "/china-dependency", label: "CCP" },
    { id: "geopolitical", path: "/geopolitical", label: "Global" },
    { id: "shortage-warning", path: "/shortage-warning", label: "Shortages" },
    { id: "risk-analyzer", path: "/risk-analyzer", label: "Risk Analyzer" },
    { id: "briefings", path: "/briefings", label: "Briefings" },
    { id: "search", path: "/search", label: "Query" },
    { id: "agent-activity", path: "/agent-activity", label: "Agents" },
  ];

  // Secondary nav items (user dropdown menu)
  const userMenuItems = [
    { id: "my-account", path: "/my-account", label: "My Account", icon: <IconUser /> },
    { id: "my-attestations", path: "/my-attestations", label: "My Attestations", icon: <IconShield /> },
    ...(user.is_admin ? [
      { id: "history", path: "/admin", label: "Admin Dashboard", icon: <IconHistory /> },
    ] : []),
  ];

  // ============================================================
  // RENDER: ROOT LANDING (workspace chooser)
  // ============================================================
  const renderLanding = () => (
    <HomeLanding
      user={user}
      onLogout={onLogout}
      totalProducts={stats.total_products}
    />
  );

  // ============================================================
  // RENDER: SUPPLY CHAIN · ANALYZE (intelligence launcher)
  // ============================================================
  const renderHome = () => (
    <HomeLauncher
      user={user}
      onLogout={onLogout}
      totalProducts={stats.total_products}
      currentShortages={stats.current_shortages}
      onSubmitQuery={(q: string) => {
        setSearchQuery(q);
        navigate("/search");
        handleSearch(q);
      }}
    />
  );

  // ============================================================
  // RENDER: DASHBOARD
  // ============================================================
  const renderDashboard = () => (
    <DashboardPage
      stats={stats}
      products={products}
      highRiskProducts={highRiskProducts}
      iariData={iariData}
      onSelectProduct={loadProductDetail}
      onNavigate={(path: string) => navigate(path)}
      AnimatedEagle={IRemedyCross}
      FloatingPill={FloatingPill}
      GlobeIcon={GlobeIcon}
      StatCard={StatCard}
      IconDatabase={IconDatabase}
      IconShield={IconShield}
      IconAlert={IconAlert}
      IconFlag={IconFlag}
      IconChevron={IconChevron}
      IconGlobe={IconGlobe}
      activityEvents={activityEvents}
    />
  );

  // ============================================================
  // RENDER: AI SEARCH
  // ============================================================
  const renderSearch = () => (
    <SearchPage
      searchInputRef={searchInputRef}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      handleSearch={handleSearch}
      isSearching={isSearching}
      searchResults={searchResults}
      copyToClipboard={copyToClipboard}
      SectionHeader={SectionHeader}
      IconSearch={IconSearch}
      IconSpinner={IconSpinner}
      IconBot={IconBot}
      IconStar={IconStar}
      onNavigateToAccount={() => navigate("/my-account")}
      onNavigateToProduct={(id: number) => loadProductDetail(id)}
    />
  );

  // ============================================================
  // RENDER: PRODUCTS LIST
  // ============================================================
  const renderProducts = () => (
    <ProductsPage
      loading={loading}
      products={products}
      productsTotal={productsTotal}
      unifiedSearch={unifiedSearch}
      unifiedSearchQuery={unifiedSearchQuery}
      isUnifiedSearching={isUnifiedSearching}
      collapsedSections={collapsedSections}
      toggleSection={toggleSection}
      setUnifiedSearchQuery={setUnifiedSearchQuery}
      setProductFilter={setProductFilter}
      setUnifiedSearch={setUnifiedSearch}
      loadProductDetail={loadProductDetail}
      loadClinicalDrugDetail={loadClinicalDrugDetail}
      SectionHeader={SectionHeader}
      IconSearch={IconSearch}
      IconSpinner={IconSpinner}
      IconChevron={IconChevron}
      RiskBadge={RiskBadge}
      ScoreRing={ScoreRing}
      StatCard={StatCard}
      IconDatabase={IconDatabase}
      IconShield={IconShield}
      IconAlert={IconAlert}
      IconFlag={IconFlag}
    />
  );

  // RENDER: PRODUCT DETAIL — hoisted to the module-scope <ProductDetailRoute/>
  // (stable component identity; see comment above the component definition).

  // ============================================================
  // RENDER: CLINICAL DRUG DETAIL
  // ============================================================
  const renderClinicalDrugDetail = () => (
    <ClinicalDrugDetailPage
      cd={selectedClinicalDrug}
      onBack={() => navigate("/products")}
      onSelectProduct={(id) => loadProductDetail(id)}
      ScoreRing={ScoreRing}
      RiskBadge={RiskBadge}
      IconArrowLeft={IconArrowLeft}
      IconChevron={IconChevron}
      IconFlag={IconFlag}
      IconGlobe={IconGlobe}
      IconCheck={IconCheck}
    />
  );

  // ============================================================
  // RENDER: DATA INGESTION
  // ============================================================
  const renderIngestion = () => (
    <IngestionPage
      SectionHeader={SectionHeader}
      ingestionMode={ingestionMode}
      setIngestionMode={setIngestionMode}
      setLookupStatus={setLookupStatus}
      setIngestionResults={setIngestionResults}
      setBatchResults={setBatchResults}
      AnimatedEagle={IRemedyCross}
      drugNameInput={drugNameInput}
      setDrugNameInput={setDrugNameInput}
      handleNDCLookup={handleNDCLookup}
      ndcInput={ndcInput}
      setNdcInput={setNdcInput}
      isIngesting={isIngesting}
      ingestionResults={ingestionResults}
      lookupStatus={lookupStatus}
      IconSpinner={IconSpinner}
      IconBot={IconBot}
      IconUpload={IconUpload}
      fileInputRef={fileInputRef}
      handleFileUpload={handleFileUpload}
      isBatchProcessing={isBatchProcessing}
      batchResults={batchResults}
      handleBatchProcess={handleBatchProcess}
      batchProgress={batchProgress}
      toggleAllSelected={toggleAllSelected}
      toggleItemExpanded={toggleItemExpanded}
      toggleItemSelected={toggleItemSelected}
      toggleScraperExpanded={toggleScraperExpanded}
      copyToClipboard={copyToClipboard}
      handleBatchCommit={handleBatchCommit}
      setRawInput={setRawInput}
      rawInput={rawInput}
      ingestionUrl={ingestionUrl}
      setIngestionUrl={setIngestionUrl}
      IconChevron={IconChevron}
      IconCheck={IconCheck}
      confirmIngestion={confirmIngestion}
      RiskBadge={RiskBadge}
      commitStatus={commitStatus}
      setCommitStatus={setCommitStatus}
    />
  );

  // ============================================================
  // RENDER: QUERY HISTORY (from Admin)
  // ============================================================
  const renderQueryHistory = () => {
    if (user?.is_admin) {
      return <Admin isAdmin={user.is_admin} />;
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <SectionHeader sub="View and analyze all AI queries made through the system">Admin Dashboard</SectionHeader>
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 40, textAlign: "center" }}>
          <IconHistory />
          <h3 style={{ fontSize: 18, fontWeight: 700, color: T.navy, marginTop: 16 }}>Admin Dashboard</h3>
          <p style={{ fontSize: 14, color: T.textMid, marginTop: 8 }}>
            You need admin access to view the admin dashboard.
          </p>
        </div>
      </div>
    );
  };

  // ============================================================
  // RENDER: MANUFACTURERS LIST
  // ============================================================
  const renderManufacturers = () => (
    <ManufacturerListPage
      apiBase={API_BASE}
      onViewManufacturer={loadManufacturerDetail}
      SectionHeader={SectionHeader}
      IconSearch={IconSearch}
      IconSpinner={IconSpinner}
      IconChevron={IconChevron}
      StatCard={StatCard}
      IconDatabase={IconDatabase}
      IconShield={IconShield}
      IconAlert={IconAlert}
      IconFlag={IconFlag}
    />
  );

  // ============================================================
  // RENDER: COUNTRY ORIGINS LIST
  // ============================================================
  const renderCountryOrigins = () => (
    <CountryOriginListPage
      apiBase={API_BASE}
      SectionHeader={SectionHeader}
      IconSearch={IconSearch}
      IconSpinner={IconSpinner}
      StatCard={StatCard}
      IconGlobe={IconGlobe}
      IconDatabase={IconDatabase}
      IconAlert={IconAlert}
      IconFlag={IconFlag}
      IconShield={IconShield}
    />
  );

  // RENDER: MANUFACTURER DETAIL — hoisted to module-scope <ManufacturerDetailRoute/>.

  // ============================================================
  // RENDER: SCRAPER HISTORY (extracted to separate component)
  // ============================================================
  const renderScraperHistory = () => (
    <ScraperHistory
      scraperLoading={scraperLoading}
      scraperOverview={scraperOverview}
      scraperRuns={scraperRuns}
      scraperErrors={scraperErrors}
    />
  );

  // ============================================================
  // RENDER: MY ACCOUNT
  // ============================================================
  const renderMyAccount = () => (
    <MyAccount />
  );

  // ============================================================
  // RENDER: RISK ANALYZER
  // ============================================================
  const renderRiskAnalyzer = () => (
    <RiskAnalyzerPage
      onSelectProduct={loadProductDetail}
      SectionHeader={SectionHeader}
      ScoreRing={ScoreRing}
      StatCard={StatCard}
      IconUpload={IconUpload}
      IconAlert={IconAlert}
      IconShield={IconShield}
      IconDatabase={IconDatabase}
      IconSpinner={IconSpinner}
    />
  );

  // ============================================================
  // RENDER: CHINA DEPENDENCY
  // ============================================================
  const renderChinaDependency = () => (
    <ChinaDependencyPage
      onSelectProduct={loadProductDetail}
      SectionHeader={SectionHeader}
      ScoreRing={ScoreRing}
      StatCard={StatCard}
      RiskBadge={RiskBadge}
      IconAlert={IconAlert}
      IconShield={IconShield}
      IconGlobe={IconGlobe}
      IconSpinner={IconSpinner}
    />
  );

  // ============================================================
  // RENDER: GLOBAL INTELLIGENCE
  // ============================================================
  const renderGeopolitical = () => (
    <GeopoliticalIntelligencePage
      SectionHeader={SectionHeader}
      StatCard={StatCard}
      IconSpinner={IconSpinner}
      IconAlert={IconAlert}
      IconGlobe={IconGlobe}
      IconFlag={IconFlag}
      IconShield={IconShield}
    />
  );

  // ============================================================
  // RENDER: SHORTAGE EARLY WARNING
  // ============================================================
  const renderShortageWarning = () => (
    <ShortageEarlyWarningPage
      onSelectProduct={loadProductDetail}
      SectionHeader={SectionHeader}
      ScoreRing={ScoreRing}
      StatCard={StatCard}
      RiskBadge={RiskBadge}
      IconSpinner={IconSpinner}
      IconAlert={IconAlert}
      IconShield={IconShield}
    />
  );

  // ============================================================
  // RENDER: SELF-ATTESTATIONS (Admin only)
  // ============================================================
  const renderSelfAttestations = () => (
    <SelfAttestationsPage />
  );

  // ============================================================
  // RENDER: MY ATTESTATIONS (All users)
  // ============================================================
  const renderMyAttestations = () => (
    <MyAttestationsPage />
  );

  // ============================================================
  // RENDER: EXECUTIVE BRIEFINGS
  // ============================================================
  const renderAgentActivity = () => (
    <AgentActivityDashboard />
  );

  const renderAgentDetail = () => (
    <AgentDetail />
  );

  const renderScoredProducts = () => (
    <ScoredProducts />
  );

  const renderFailedScoring = () => (
    <FailedScoring />
  );

  const renderEvidenceDefinitions = () => (
    <EvidenceDefinitions />
  );

  const renderSuggestedImprovements = () => (
    <SuggestedScoringImprovements />
  );

  const renderDataAnalyst = () => (
    <DataAnalyst />
  );

  const renderStewardDataAnalyst = () => (
    <StewardDataAnalyst />
  );

  const renderStewardProductUpdates = () => (
    <StewardProductUpdates />
  );

  const renderStewardFailedUpdates = () => (
    <StewardFailedUpdates />
  );

  const renderStewardTotalProducts = () => (
    <StewardTotalProducts />
  );

  const renderDataOrchestratorDetail = () => (
    <DataOrchestratorDetail />
  );

  const renderEvidenceHistory = () => (
    <EvidenceHistory />
  );

  const renderApiIntelligence = () => (
    <ApiIntelligencePage
      SectionHeader={SectionHeader}
    />
  );

  const renderBriefings = () => (
    <ExecutiveBriefingsPage
      SectionHeader={SectionHeader}
      IconSpinner={IconSpinner}
      IconAlert={IconAlert}
      IconShield={IconShield}
    />
  );

  // ============================================================
  // MAIN LAYOUT
  // ============================================================
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", fontFamily: "'Inter', -apple-system, sans-serif", background: T.offWhite }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,700;0,800;1,400;1,700&family=Inter:wght@400;500;600;700;800&display=swap');
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes spinSlow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes floatPill { 0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.12; } 50% { transform: translateY(-20px) rotate(15deg); opacity: 0.2; } }
        @keyframes scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input::placeholder, textarea::placeholder { color: ${T.textLight}; }
        ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 3px; }
        .nav-desktop-links { display: flex; }
        .nav-desktop-user-email { display: inline; }
        .nav-mobile-hamburger { display: none; }
        .nav-mobile-menu { display: none; }
        @media (max-width: 1024px) {
          .nav-desktop-links { display: none !important; }
          .nav-desktop-user-email { display: none !important; }
          .nav-mobile-hamburger { display: flex !important; }
          .nav-mobile-menu { display: flex !important; }
          .main-content-area { padding: 24px 20px !important; }
        }
        @media (max-width: 480px) {
          .nav-brand-text { display: none !important; }
          .main-content-area { padding: 16px 12px !important; }
        }
      `}</style>

      {/* Surveillance Ticker */}
      <div style={{
        background: T.navy, padding: '6px 0', overflow: 'hidden',
        whiteSpace: 'nowrap', borderBottom: `2px solid ${T.red}`, flexShrink: 0,
      }}>
        <div style={{
          display: 'inline-block', animation: 'scroll 45s linear infinite',
          fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
          fontSize: 11, color: T.red, letterSpacing: '0.08em', fontWeight: 500,
        }}>
          {'  \u25C6 TRADESPY.AI \u2014 ACTIVE SURVEILLANCE   \u25C6 149,000+ NDCs UNDER CONTINUOUS MONITORING   \u25C6 30+ AUTONOMOUS AGENTS OPERATIONAL   \u25C6 CCP HARD FLOOR: ENFORCED IN CODE, NOT POLICY   \u25C6 THREAT LEVEL: ELEVATED   '.repeat(2)}
        </div>
      </div>

      {/* Formal Document Header — hidden on home (the launcher renders its own header) */}
      {!isHome && (
      <div style={{
        background: T.offWhite, borderBottom: `3px solid ${T.navy}`,
        padding: '16px 32px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', position: 'relative', flexShrink: 0,
      }}>
        {/* Left: Case File Info */}
        <div style={{
          fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
          fontSize: 11, color: T.textMid, lineHeight: 1.7, letterSpacing: '0.06em',
        }}>
          <div style={{ fontWeight: 600, color: T.textDark, textTransform: 'uppercase' }}>CASE FILE NO. TS-2026-001</div>
          <div>CLASSIFICATION: <span
            onMouseEnter={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.red; }}
            onMouseLeave={e => { e.currentTarget.style.background = T.navy; e.currentTarget.style.color = T.navy; }}
            style={{ background: T.navy, color: T.navy, padding: '2px 8px', fontSize: 10, cursor: 'default', transition: 'background 0.3s ease, color 0.3s ease', fontWeight: 700 }}>TOP SECRET</span></div>
          <div>JURISDICTION: UNITED STATES</div>
          <div style={{ fontSize: 9, color: T.textMid }}>Powered by iRemedy Healthcare Companies, Inc.</div>
        </div>

        {/* Center: Logo */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
          position: 'absolute', left: '50%', transform: 'translateX(-50%)', cursor: 'pointer',
        }} onClick={() => { navigate("/"); setMobileMenuOpen(false); }} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') navigate("/"); }}>
          <img src="/tradespy-logo-text.png" alt="TheTradeSpy.ai" style={{ height: 48 }} />
          <div style={{
            fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
            fontSize: 9.6, color: T.textMid, textTransform: 'uppercase', letterSpacing: '1.9px',
          }}>
            Supply Chain Intelligence Platform
          </div>
        </div>

        {/* Right: American Flag */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img src="/us-flag.png" alt="United States Flag" style={{ height: 82, objectFit: 'contain', border: '1px solid #ccc' }} />
        </div>
      </div>
      )}

      {/* Navigation Bar — centered, no logo. Hidden on home (nav items become app tiles) */}
      {!isHome && (
      <nav style={{
        background: T.navy, display: 'flex', justifyContent: 'center',
        alignItems: 'center', height: 42, flexShrink: 0,
        borderBottom: `1px solid rgba(200, 22, 10, 0.3)`,
        position: 'relative',
      }}>
        <div className="nav-desktop-links" style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          {navItems.map(item => {
            const active = view === item.id;
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                style={{
                  padding: '8px 16px', border: 'none',
                  background: 'transparent',
                  color: active ? T.white : T.textMid,
                  fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
                  fontSize: 11, fontWeight: active ? 700 : 500,
                  cursor: 'pointer', transition: 'all 0.15s',
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  borderBottom: active ? `2px solid ${T.white}` : '2px solid transparent',
                  textDecoration: 'none',
                }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.color = T.offWhite; e.currentTarget.style.background = 'rgba(200,22,10,0.1)'; } }}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.color = T.textMid; e.currentTarget.style.background = 'transparent'; } }}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {/* Right side: user controls */}
        <div style={{ position: 'absolute', right: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="nav-desktop-user-email" style={{
            fontSize: 11, color: T.textMid,
            fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
          }}>{user.email}</span>

          {/* Desktop: User menu toggle */}
          <button
            className="nav-desktop-links"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            style={{
              padding: "6px 14px", borderRadius: 6,
              border: `1px solid rgba(200, 22, 10, 0.3)`,
              background: userMenuOpen ? "rgba(200, 22, 10, 0.1)" : "transparent",
              color: T.textMid, cursor: "pointer",
              fontSize: 11, fontFamily: "'IBM Plex Mono', 'Courier New', monospace", transition: "all 0.2s",
              display: "flex", alignItems: "center", gap: 6,
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(200, 22, 10, 0.5)"; (e.currentTarget as HTMLButtonElement).style.color = T.offWhite; }}
            onMouseLeave={e => { if (!userMenuOpen) { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(200, 22, 10, 0.3)"; (e.currentTarget as HTMLButtonElement).style.color = T.textMid; } }}
          >
            <IconUser />
            <IconChevron />
          </button>

          {/* Mobile: Hamburger toggle */}
          <button
            className="nav-mobile-hamburger"
            onClick={() => { setMobileMenuOpen(!mobileMenuOpen); setUserMenuOpen(false); }}
            style={{
              padding: 8, borderRadius: 6, border: `1px solid rgba(200, 22, 10, 0.3)`,
              background: mobileMenuOpen ? "rgba(200, 22, 10, 0.1)" : "transparent",
              color: T.textMid, cursor: "pointer",
              display: "none", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s",
            }}
          >
            {mobileMenuOpen ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="5" y1="5" x2="15" y2="15" />
                <line x1="15" y1="5" x2="5" y2="15" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="3" y1="5" x2="17" y2="5" />
                <line x1="3" y1="10" x2="17" y2="10" />
                <line x1="3" y1="15" x2="17" y2="15" />
              </svg>
            )}
          </button>

          {/* Desktop: User dropdown menu */}
          {userMenuOpen && (
            <div
              style={{
                position: "absolute", top: "calc(100% + 8px)", right: 0,
                background: T.white, borderRadius: T.radius,
                border: `1px solid ${T.border}`, boxShadow: T.shadowLg,
                minWidth: 220, padding: "8px 0", zIndex: 200,
              }}
              onMouseLeave={() => setUserMenuOpen(false)}
            >
              {/* User info header */}
              <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.textDark }}>{user.email}</div>
                <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>{user.is_admin ? 'Administrator' : 'User'}</div>
              </div>

              {/* Menu items */}
              {userMenuItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => { navigate(item.path); setUserMenuOpen(false); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 16px", border: "none", background: "transparent",
                    color: view === item.id ? T.navy : T.textMid,
                    cursor: "pointer", width: "100%", textAlign: "left",
                    fontSize: 13, fontWeight: view === item.id ? 700 : 500,
                    fontFamily: "inherit", transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = T.offWhite; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                >
                  <span style={{ color: T.textLight, flexShrink: 0 }}>{item.icon}</span>
                  {item.label}
                </button>
              ))}

              {/* Sign Out */}
              <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 4, paddingTop: 4 }}>
                <button
                  onClick={onLogout}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 16px", border: "none", background: "transparent",
                    color: T.danger, cursor: "pointer", width: "100%", textAlign: "left",
                    fontSize: 13, fontWeight: 500, fontFamily: "inherit", transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = T.dangerBg; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                >
                  <IconLogout />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>
      )}

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="nav-mobile-menu" style={{
          display: "none", flexDirection: "column",
          background: T.navy, borderBottom: `1px solid rgba(200, 22, 10, 0.2)`,
          position: "sticky", top: 0, zIndex: 99,
          maxHeight: "80vh", overflowY: "auto",
        }}>
          {/* Primary Nav Items */}
          <div style={{ padding: "8px 16px", borderBottom: "1px solid rgba(200, 22, 10, 0.15)" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textMid, textTransform: "uppercase", letterSpacing: "0.1em", padding: "8px 12px", fontFamily: "'IBM Plex Mono', 'Courier New', monospace" }}>Navigation</div>
            {navItems.map(item => {
              const active = view === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "12px 12px", borderRadius: 8, border: "none", width: "100%",
                    background: active ? "rgba(200, 22, 10, 0.1)" : "transparent",
                    color: active ? T.white : T.textMid,
                    cursor: "pointer", fontSize: 13, fontWeight: active ? 700 : 500,
                    fontFamily: "'IBM Plex Mono', 'Courier New', monospace", textAlign: "left", transition: "all 0.15s",
                    textTransform: "uppercase", letterSpacing: "0.08em",
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* User Section */}
          <div style={{ padding: "8px 16px", borderBottom: "1px solid rgba(200, 22, 10, 0.15)" }}>
            <div style={{ padding: "8px 12px", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(200, 22, 10, 0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: T.textMid }}>
                <IconUser />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.offWhite, fontFamily: "'IBM Plex Mono', 'Courier New', monospace" }}>{user.email}</div>
                <div style={{ fontSize: 11, color: T.textMid }}>{user.is_admin ? 'Administrator' : 'User'}</div>
              </div>
            </div>
            {userMenuItems.map(item => {
              const active = view === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "12px 12px", borderRadius: 8, border: "none", width: "100%",
                    background: active ? "rgba(200, 22, 10, 0.1)" : "transparent",
                    color: active ? T.red : T.textMid,
                    cursor: "pointer", fontSize: 13, fontWeight: active ? 700 : 500,
                    fontFamily: "'IBM Plex Mono', 'Courier New', monospace", textAlign: "left", transition: "all 0.15s",
                  }}
                >
                  <span style={{ color: T.textMid, flexShrink: 0 }}>{item.icon}</span>
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* Sign Out */}
          <div style={{ padding: "8px 16px" }}>
            <button
              onClick={() => { setMobileMenuOpen(false); onLogout(); }}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "12px 12px", borderRadius: 8, border: "none", width: "100%",
                background: "transparent",
                color: T.danger, cursor: "pointer", fontSize: 13, fontWeight: 500,
                fontFamily: "'IBM Plex Mono', 'Courier New', monospace", textAlign: "left", transition: "all 0.15s",
              }}
            >
              <IconLogout />
              Sign Out
            </button>
          </div>
        </div>
      )}

      {/* Global Filter Bar — persistent across all pages except the home launcher */}
      {!isHome && <GlobalFilterBar />}

      {/* Global Pipeline Toast — visible on every page */}
      <PipelineToast />

      {/* Main Content */}
      <div className="main-content-area" style={{ flex: 1, overflow: "auto", padding: isHome ? 0 : "32px 40px" }} onClick={() => { if (mobileMenuOpen) setMobileMenuOpen(false); }}>
        <div style={{ maxWidth: isHome ? "none" : 1200, margin: "0 auto" }}>
          <Routes>
            <Route path="/" element={renderLanding()} />
            <Route path="/supplychain" element={renderHome()} />
            <Route path="/dashboard" element={renderDashboard()} />
            <Route path="/search" element={renderSearch()} />
            <Route path="/products" element={renderProducts()} />
            <Route path="/products/:id" element={<ProductDetailRoute selectedProduct={selectedProduct} setSelectedProduct={setSelectedProduct} loadProductDetail={loadProductDetail} loadClinicalDrugDetail={loadClinicalDrugDetail} loadManufacturerDetail={loadManufacturerDetail} />} />
            <Route path="/clinical-drug/:id" element={renderClinicalDrugDetail()} />
            <Route path="/manufacturers" element={renderManufacturers()} />
            <Route path="/manufacturers/:id" element={<ManufacturerDetailRoute loadProductDetail={loadProductDetail} loadManufacturerDetail={loadManufacturerDetail} />} />
            <Route path="/country-origins" element={renderCountryOrigins()} />
            <Route path="/ingest" element={renderIngestion()} />
            <Route path="/scraper-history" element={renderScraperHistory()} />
            <Route path="/admin" element={renderQueryHistory()} />
            <Route path="/my-account" element={renderMyAccount()} />
            <Route path="/risk-analyzer" element={renderRiskAnalyzer()} />
            <Route path="/apis" element={renderApiIntelligence()} />
            <Route path="/china-dependency" element={renderChinaDependency()} />
            <Route path="/geopolitical" element={renderGeopolitical()} />
            <Route path="/shortage-warning" element={renderShortageWarning()} />
            <Route path="/self-attestations" element={renderSelfAttestations()} />
            <Route path="/my-attestations" element={renderMyAttestations()} />
            <Route path="/briefings" element={renderBriefings()} />
            <Route path="/agent-activity" element={renderAgentActivity()} />
            <Route path="/agent-detail/data_orchestrator" element={renderDataOrchestratorDetail()} />
            <Route path="/evidence-history" element={renderEvidenceHistory()} />
            <Route path="/agent-detail/:agentName" element={renderAgentDetail()} />
            <Route path="/agent-detail/:agentName/scored-products" element={renderScoredProducts()} />
            <Route path="/agent-detail/:agentName/product-updates" element={renderStewardProductUpdates()} />
            <Route path="/agent-detail/:agentName/failed-updates" element={renderStewardFailedUpdates()} />
            <Route path="/agent-detail/:agentName/total-products" element={renderStewardTotalProducts()} />
            <Route path="/agent-detail/:agentName/failed-scoring" element={renderFailedScoring()} />
            <Route path="/agent-detail/:agentName/evidence-definitions" element={renderEvidenceDefinitions()} />
            <Route path="/agent-detail/:agentName/suggested-improvements" element={renderSuggestedImprovements()} />
            <Route path="/data-analyst/:agentName" element={renderDataAnalyst()} />
            <Route path="/steward-data-analyst/:agentName" element={renderStewardDataAnalyst()} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>

      {/* Senior Analyst */}
      <Chatbot user={user} currentPath={location.pathname} />

      {/* The Director — Expert Agent */}
      <ExpertAgent user={user} currentPath={location.pathname} />
    </div>
  );
}
